import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AnalyticsApi from 'libs/api/analytics';
import useFilters from '../useFilters';
import Helpers from 'helpers';
import UIHelper from 'helpers/UIHelper';
import _ from 'lodash';
import { fileNameEdit } from 'helpers/FileUtils';
import { usePdf } from '../../../../hooks/usePdf';

const api = new AnalyticsApi('charts/kpi_weekdays_holidays/');

const format = (v) => `${v}%`;

const defaultState = {
  table1: [],
  table2: [],
  table3: [],
  table4: [],
  barChart: [],
  total: {},
  tableData: [],
  barChangeLine: [],
  keys: [],
};

const useKpiWeekdaysHolidays = (isPrint, allReportsData, isAllReports) => {
  const { t } = useTranslation();
  const { handleDownloadPdf, loadingPdf } = usePdf();
  const { filters, title, searchParams } = useFilters(
    false,
    false,
    isAllReports,
  );
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(defaultState);
  const [errors, setErrors] = useState([]);

  const handleDownloadExcel = async () => {
    setLoading(true);
    try {
      const params = {
        ...Object.fromEntries([...searchParams]),
      };
      const response = await api.getExcel({ params });
      Helpers.downloadFile(response, fileNameEdit());
    } catch (e) {
      const error = e?.response?.data?.errors || [t('Something went wrong!')];
      UIHelper.openNotificationWithIcon('error', error.join('\n'));
    } finally {
      setLoading(false);
    }
  };

  const [columns, setColumns] = useState([
    {
      title: t('Periodo'),
      dataIndex: 'Periodo',
      width: 200,
    },
    {
      title: t('GG'),
      dataIndex: 'GG',
      width: 100,
      render: (v) => Helpers.numberWithCommas(v, ''),
    },
    {
      title: t('Footfall Village'),
      dataIndex: 'Footfall Village',
      width: 200,
      render: (v) => Helpers.numberWithCommas(v, ''),
    },
    {
      title: t('Net sales(€)'),
      dataIndex: 'Net sales(€)',
      width: 200,
      render: (v) => Helpers.numberWithCommas(v, '€'),
    },
    {
      title: t('Tickets'),
      dataIndex: 'Tickets',
      width: 100,
      render: (v) => Helpers.numberWithCommas(v, ''),
    },
    {
      title: t('Pezzi'),
      dataIndex: 'Pezzi',
      width: 100,
      render: (v) => Helpers.numberWithCommas(v, ''),
    },
    {
      title: t('ATV (€)'),
      dataIndex: 'ATV',
      width: 100,
      render: (v) => Helpers.numberWithCommas(v, ' €'),
    },
    {
      title: t('UPT'),
      dataIndex: 'UPT',
      width: 100,
      render: (v) => Helpers.numberWithCommas(v, ''),
    },
    {
      title: t('CR(%)'),
      dataIndex: 'CR(%)',
      width: 100,
      render: (v) => Helpers.numberWithCommas(v, '%'),
    },
    {
      title: t('Spesa Media per Visitatore'),
      dataIndex: 'Spesa Media per Visitatore',
      width: 200,
      render: (v) => Helpers.numberWithCommas(v, ' €'),
    },
  ]);

  const [columns2, setColumns2] = useState([
    {
      title: t('Periodo'),
      dataIndex: 'Periodo',
      width: 200,
    },
    {
      title: t('GG'),
      dataIndex: 'GG',
      width: 100,
      render: (v) =>
        Helpers.numberWithCommas(Helpers.changeDotToComa(format(v))),
    },
    {
      title: t('Footfall Village'),
      dataIndex: 'Footfall Village',
      width: 200,
      render: (v) =>
        Helpers.numberWithCommas(Helpers.changeDotToComa(format(v))),
    },
    {
      title: t('Net sales(€)'),
      dataIndex: 'Net sales(€)',
      width: 200,
      render: (v) =>
        Helpers.numberWithCommas(Helpers.changeDotToComa(format(v))),
    },
    {
      title: t('Tickets'),
      dataIndex: 'Tickets',
      width: 100,
      render: (v) =>
        Helpers.numberWithCommas(Helpers.changeDotToComa(format(v))),
    },
    {
      title: t('Pezzi'),
      dataIndex: 'Pezzi',
      width: 100,
      render: (v) =>
        Helpers.numberWithCommas(Helpers.changeDotToComa(format(v))),
    },
    {
      title: t('ATV'),
      dataIndex: 'ATV',
      width: 100,
      render: (v) => Helpers.changeDotToComa(format(v)),
    },
    {
      title: t('UPT'),
      dataIndex: 'UPT',
      width: 100,
      render: (v) => Helpers.changeDotToComa(format(v)),
    },
    {
      title: t('CR(%)'),
      dataIndex: 'CR(%)',
      width: 100,
      render: (v) => Helpers.numberWithCommas(v, '%'),
    },
    {
      title: t('Spesa Media per Visitatore'),
      dataIndex: 'Spesa Media per Visitatore',
      width: 200,
      render: (v) => Helpers.numberWithCommas(v, ' €'),
    },
  ]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = {
          ...Object.fromEntries([...searchParams]),
        };
        if (!params.by_period || !params.by_shopping_center) return;

        let chart, table1, table2, table3, table4;

        if (!isAllReports) {
          const {
            data: {
              chart: backChart,
              table1: backTable1,
              table2: backTable2,
              table3: backTable3,
              table4: backTable4,
            },
          } = await api.getFootfalls({ params });
          chart = backChart;
          table1 = backTable1;
          table2 = backTable2;
          table3 = backTable3;
          table4 = backTable4;
        } else {
          const {
            chart: backChart,
            table1: backTable1,
            table2: backTable2,
            table3: backTable3,
            table4: backTable4,
          } = allReportsData;
          chart = backChart;
          table1 = backTable1;
          table2 = backTable2;
          table3 = backTable3;
          table4 = backTable4;
        }

        if (!table1 || !table2 || !table3 || !table4) return;

        const maxValueFroLy = _.maxBy(chart, 'FERIALI')['FERIALI'];
        const maxValueFroYtd = _.maxBy(chart, 'FERIALI')['FERIALI'];

        const maxValue =
          maxValueFroLy > maxValueFroYtd ? maxValueFroLy : maxValueFroYtd;

        const barChart = [{ date: 'FERIALI' }, { date: 'FESTIVI' }];
        const keys = [];
        const colors = ['#61cdbb', '#f5222d', '#fa8c16'];
        chart.forEach((i, index) => {
          keys.push(i.title);
          barChart[0][i.title] = i['FERIALI'] ? i['FERIALI'] : 0;
          barChart[0][i.title + 'Color'] = colors[index];
          barChart[1][i.title] = i['FESTIVI'] ? i['FESTIVI'] : 0;
          barChart[1][i.title + 'Color'] = colors[index];
        });

        setData({
          table1,
          table2,
          table3,
          table4,
          maxValue,
          barChart,
          tableData: [],
          barChangeLine: [],
          keys,
        });
        setErrors([]);
      } catch (e) {
        console.log(e);
        const error = e?.response?.data?.date || [];
        if (error.length) {
          UIHelper.openNotificationWithIcon('error', error.join(', '));
        }
        setData(defaultState);
        setErrors(UIHelper.showAnalyticsError(e));
      } finally {
        setLoading(false);
        if (isPrint) {
          const event = new CustomEvent('status', { detail: 'ok' });

          setTimeout(() => {
            window.dispatchEvent(event);
          }, 5000);
        }
      }
    })();
  }, [searchParams, isPrint, allReportsData, isAllReports]);

  return {
    columns,
    data,
    filters,
    format,
    title,
    loading,
    handleDownloadExcel,
    searchParams,
    handleDownloadPdf,
    loadingPdf,
    errors,
    columns2,
  };
};

export default useKpiWeekdaysHolidays;
